// On-device storage + microphone capture for recordings, and the bridge that
// makes a recording OVERRIDE the standard library sound during playback.
//
// A "target" is one recordable sound, identified by the same key the audio engine
// uses: a phoneme is 'ph:<code>' (shared across words), a whole word is
// 'word:<id>'. Up to 3 live "takes" per target live in IndexedDB; deleting a take
// is a SOFT delete (kept, recoverable). A per-target pref chooses 'recorded'
// (default once a take exists) vs 'standard', and which take is active.

import { decodeBlob, setOverride, clearOverride } from './audio.js'

const DB_NAME = 'phonic-recordings'
const SAMPLES = 'samples'
const PREFS = 'prefs'
const VERSION = 2
const MAX = 3
export const MAX_SAMPLES = MAX

let dbP = null
function db() {
  if (dbP) return dbP
  dbP = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION)
    req.onupgradeneeded = () => {
      const d = req.result
      if (!d.objectStoreNames.contains(SAMPLES)) d.createObjectStore(SAMPLES, { keyPath: 'id' })
      if (!d.objectStoreNames.contains(PREFS)) d.createObjectStore(PREFS, { keyPath: 'targetKey' })
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbP
}
function tx(name, mode) { return db().then((d) => d.transaction(name, mode).objectStore(name)) }
function reqP(request) { return new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error) }) }
const idFor = (targetKey, slot) => `${targetKey}::${slot}`

export const safeName = (key) => String(key).replace(/[^a-z0-9_-]/gi, '_')

export async function listSamples(targetKey) {
  const s = await tx(SAMPLES, 'readonly')
  const all = await reqP(s.getAll())
  return all.filter((r) => r.targetKey === targetKey).sort((a, b) => a.slot - b.slot)
}
export async function allTargetKeys() {
  const s = await tx(SAMPLES, 'readonly')
  const all = await reqP(s.getAll())
  return [...new Set(all.map((r) => r.targetKey).filter(Boolean))]
}
export async function putSample(sample) {
  const s = await tx(SAMPLES, 'readwrite')
  const rec = { ...sample, id: idFor(sample.targetKey, sample.slot) }
  await reqP(s.put(rec))
  return rec
}
export async function getPref(targetKey) {
  const s = await tx(PREFS, 'readonly')
  const p = await reqP(s.get(targetKey))
  return p || { targetKey, mode: 'recorded', activeSlot: 0 } // recorded-by-default once a take exists
}
export async function setPref(targetKey, patch) {
  const cur = await getPref(targetKey)
  const next = { ...cur, ...patch, targetKey }
  const s = await tx(PREFS, 'readwrite')
  await reqP(s.put(next))
  return next
}

// ---- pure helpers over a sample list ----
export function liveSamples(samples) { return samples.filter((s) => !s.deleted) }
export function activeSample(samples, pref) {
  const live = liveSamples(samples)
  if (!live.length) return null
  const chosen = live.find((s) => s.slot === pref.activeSlot)
  return chosen || live.reduce((a, b) => ((b.ts || 0) > (a.ts || 0) ? b : a)) // else most recent
}
export function canRecord(samples) { return liveSamples(samples).length < MAX }
export function nextSlot(samples) { return samples.length ? Math.max(...samples.map((s) => s.slot)) + 1 : 1 }

// ---- audio-override bridge ----
// A phoneme recording overrides both the tap key and the blend key for its code.
function keysFor(targetKey) {
  if (targetKey.startsWith('ph:')) { const code = targetKey.slice(3); return [`ph:${code}`, `tph:${code}`] }
  return [targetKey]
}
export async function applyTarget(targetKey) {
  const keys = keysFor(targetKey)
  const [samples, pref] = await Promise.all([listSamples(targetKey), getPref(targetKey)])
  const active = pref.mode === 'standard' ? null : activeSample(samples, pref)
  if (!active) { keys.forEach(clearOverride); return }
  try {
    const buffer = await decodeBlob(active.blob)
    keys.forEach((k) => setOverride(k, buffer))
  } catch (_) {
    keys.forEach(clearOverride) // undecodable → fall back to the standard sound
  }
}
export async function applyAll() {
  const targets = await allTargetKeys()
  for (const t of targets) { try { await applyTarget(t) } catch (_) { /* skip */ } }
}

// ---- microphone capture ----
// Why recording is unavailable, or '' when it's fine. The usual culprit on a
// phone is an INSECURE ORIGIN: getUserMedia only exists on https:// or localhost,
// so opening the dev server at http://<lan-ip> hides the microphone entirely.
export function recorderIssue() {
  if (typeof window === 'undefined') return 'This device can’t record.'
  if (typeof window.MediaRecorder === 'undefined') return 'This browser can’t record audio.'
  if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
    if (window.isSecureContext === false) return 'Recording needs a secure page — open the app over https:// or localhost.'
    return 'This browser can’t reach the microphone.'
  }
  if (window.isSecureContext === false) return 'Recording needs a secure page — open the app over https:// or localhost.'
  return ''
}
export function recorderSupported() { return recorderIssue() === '' }

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
export function pickMime() {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const m of MIME_CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(m)) return m } catch (_) { /* keep looking */ }
  }
  return ''
}
export function extFor(mime) {
  if (!mime) return 'webm'
  if (mime.includes('wav')) return 'wav'
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('mp4')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  return 'webm'
}

// Acquire the mic now (so the permission prompt happens up front), but DON'T start
// capturing until begin() is called — lets the UI run a countdown first so the
// count isn't part of the recording. Controller: { mime, begin(), stop(), cancel() }.
export async function startRecording() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const mime = pickMime()
  const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream)
  const chunks = []
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data) }
  const type = () => rec.mimeType || mime || 'audio/webm'

  // Single, idempotent finish path: releases the mic and resolves the in-flight
  // stop() promise (if any). onstop is bound once and never nulled, so cancel()
  // can't strip the resolver and leave it dangling.
  let settle = null
  const finish = () => {
    stream.getTracks().forEach((t) => t.stop())
    if (settle) { const r = settle; settle = null; r(new Blob(chunks, { type: type() })) }
  }
  rec.onstop = finish

  return {
    mime: type(),
    begin() { if (rec.state === 'inactive') rec.start() }, // start capturing (after the countdown)
    stop() {
      return new Promise((resolve) => {
        settle = resolve
        if (rec.state === 'inactive') { finish(); return } // never begun / already stopped
        try { rec.stop() } catch (_) { finish() }
      })
    },
    cancel() {
      try { if (rec.state !== 'inactive') rec.stop() } catch (_) { /* ignore */ }
      finish()
    },
  }
}
